/**
 * Agent Registry for Ameen Insurance Assistant
 * 
 * Creates and registers all agent nodes.
 * The initial agent is always greeting_agent.
 */

import { AgentNode, VoiceGender } from './types';
import {
  createGreetingAgent,
  createRouterAgent,
  createPolicyAgent,
  createDocumentAgent,
  createUnderWritingQuestionAgent,
  createClaimAgent,
  createSubmitPolicyAgent,
  createSubmitClaimAgent,
  createVehicleInspectionAgent,
  createFAQAgent,
} from './nodes';

/**
 * Available agent IDs (matches backend AGENT_CONFIGS)
 */
export const AGENT_IDS = {
  GREETING: 'greeting_agent',
  ROUTER: 'router_agent',
  POLICY: 'policy_agent',
  DOCUMENT: 'document_agent',
  SUBMIT_POLICY: 'submit_policy_agent',
  CLAIM: 'claim_agent',
  SUBMIT_CLAIM: 'submit_claim_agent',
  VEHICLE_INSPECTION: 'vehicle_inspection_agent',
  UNDER_WRITING_QUESTION: 'under_writing_question_agent',
  FAQ: 'faq_agent',
} as const;

export type AgentId = typeof AGENT_IDS[keyof typeof AGENT_IDS];

/**
 * Get the initial agent ID
 */
export function getInitialAgentId(): AgentId {
  return AGENT_IDS.GREETING;
}

/**
 * Create all agent nodes
 */
export function createAllAgents(voiceGender: VoiceGender = 'male'): Map<string, AgentNode> {
  const agents = new Map<string, AgentNode>();

  // Create all agents
  agents.set(AGENT_IDS.GREETING, createGreetingAgent(voiceGender));
  agents.set(AGENT_IDS.ROUTER, createRouterAgent(voiceGender));
  agents.set(AGENT_IDS.POLICY, createPolicyAgent(voiceGender));
  agents.set(AGENT_IDS.DOCUMENT, createDocumentAgent(voiceGender));
  agents.set(AGENT_IDS.UNDER_WRITING_QUESTION, createUnderWritingQuestionAgent(voiceGender));
  agents.set(AGENT_IDS.SUBMIT_POLICY, createSubmitPolicyAgent(voiceGender));
  agents.set(AGENT_IDS.CLAIM, createClaimAgent(voiceGender));
  agents.set(AGENT_IDS.SUBMIT_CLAIM, createSubmitClaimAgent(voiceGender));
  agents.set(AGENT_IDS.VEHICLE_INSPECTION, createVehicleInspectionAgent(voiceGender));
  agents.set(AGENT_IDS.FAQ, createFAQAgent(voiceGender));

  return agents;
}

/**
 * Get an agent by ID
 */
export function getAgent(agents: Map<string, AgentNode>, agentId: string): AgentNode | undefined {
  return agents.get(agentId);
}

/**
 * List all available agent IDs
 */
export function getAvailableAgentIds(): string[] {
  return Object.values(AGENT_IDS);
}
